import React from 'react';
import { Card } from './Card';
import { Button } from './Button';

export interface InviteBannerProps {
  /** e.g. "review", "negotiation", "contract" */
  resourceLabel: string;
  /** Display name of the party who created the pending resource */
  creatorName: string;
  /** Optional title of the resource being offered (e.g. review/contract title) */
  title?: string;
  onAccept: () => void;
  onDecline: () => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Spec 303 — shared "you've been invited" card used by Reviews, Boundaries,
 * and Contracts wherever a resource sits in a pending-acceptance/pending
 * consent-gate state for the current viewer (the invited, non-creating
 * party). Deliberately a single shared primitive so the three surfaces read
 * as one consistent pattern rather than three bespoke implementations.
 */
export const InviteBanner: React.FC<InviteBannerProps> = ({
  resourceLabel,
  creatorName,
  title,
  onAccept,
  onDecline,
  disabled,
  className,
}) => {
  return (
    <Card
      variant="bordered"
      padding="md"
      className={`border-l-4 border-l-[var(--theme-warning)] mb-3 ${className ?? ''}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="text-[15px] font-semibold text-text-primary leading-snug">
            You&apos;ve been invited to a {resourceLabel}
            {title ? `: "${title}"` : ''}
          </div>
          <div className="text-[12px] text-text-tertiary mt-0.5">
            by {creatorName}
          </div>
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="primary"
          className="flex-1"
          onClick={onAccept}
          disabled={disabled}
        >
          Accept
        </Button>
        <Button
          size="sm"
          variant="secondary"
          className="flex-1"
          onClick={onDecline}
          disabled={disabled}
        >
          Decline
        </Button>
      </div>
    </Card>
  );
};
